// OnboardingBanner (task 7.5 of the dashboard-redesign spec — Req 12.2, 12.3).
//
// A Day-1 empty-state banner rendered above the KPI band by the dashboard
// shell when the tenant has zero buildings. It welcomes a brand-new user
// and provides the primary "Add your first building" call-to-action so the
// dashboard doesn't just present eight empty widgets.
//
// Contract (from the dashboard-redesign requirements):
//   Req 12.2 — WHEN the tenant has zero buildings, the Dashboard_Page SHALL
//              render an OnboardingBanner above the KPI band with copy
//              "Get started — add your first building" and a primary CTA
//              deep-linking to /buildings/new.
//   Req 12.3 — WHEN the tenant's building count transitions from zero to
//              one, the OnboardingBanner SHALL disappear on the next
//              render pass without a full-page reload.
//
// Requirement 12.3 is satisfied by keeping the component a pure function
// of the `visible` prop — the parent (DashboardShell) recomputes
// `visible = buildings.length === 0` on every render and passes it in.
// When `visible` becomes `false`, this component returns `null` and the
// banner is removed from the DOM without any imperative dismissal step.
//
// Deep-link target: App.tsx routes `buildings/:id` to BuildingDetails, and
// BuildingDetails.tsx explicitly handles the case `id === "new"` as the
// create form (see BuildingDetails.tsx). BuildingsList.tsx also links to
// `/dashboard/buildings/new`, so this is the canonical create path.

import React from "react";
import { Link } from "react-router-dom";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";

/**
 * Props for {@link OnboardingBanner}.
 */
export interface OnboardingBannerProps {
  /**
   * When `true`, the banner renders. When `false`, the component returns
   * `null` (banner is removed from the DOM). The parent (DashboardShell)
   * computes `visible = buildings.length === 0`.
   */
  visible: boolean;
}

/**
 * Static brand colours for the banner surface. Kept as inline styles rather
 * than Tailwind arbitrary values because the palette is a one-off, muted
 * amber tint used only here.
 *
 *   Background: amber-100 (#fef3c7)
 *   Border:     amber-200 (#fde68a)
 *   Icon/heading text: amber-800 (#92400e) — matches the "Owing/Warning"
 *                       tone from the design-language.md palette.
 */
const BANNER_STYLE: React.CSSProperties = {
  backgroundColor: "#fef3c7",
  borderColor: "#fde68a",
  borderWidth: 1,
  borderStyle: "solid",
};

const HEADLINE_COLOR = "#92400e";
const SUBTEXT_COLOR = "#78350f";
const ICON_COLOR = "#b45309";

/**
 * Day-1 empty-state banner shown above the dashboard's KPI band when the
 * tenant has no buildings yet.
 *
 * @param props - see {@link OnboardingBannerProps}.
 * @returns the banner element when `visible`, otherwise `null`.
 */
export function OnboardingBanner({
  visible,
}: OnboardingBannerProps): JSX.Element | null {
  if (!visible) {
    return null;
  }

  return (
    <section
      role="region"
      aria-label="Onboarding"
      data-testid="onboarding-banner"
      className="w-full rounded-xl p-4 flex items-center gap-4"
      style={BANNER_STYLE}
    >
      <BuildingOffice2Icon
        aria-hidden="true"
        data-testid="onboarding-banner-icon"
        className="h-10 w-10 flex-shrink-0"
        style={{ color: ICON_COLOR }}
      />

      <div className="flex-1 min-w-0">
        <h2
          data-testid="onboarding-banner-headline"
          className="text-base font-semibold"
          style={{ color: HEADLINE_COLOR }}
        >
          Get started — add your first building
        </h2>
        <p
          data-testid="onboarding-banner-subtext"
          className="text-sm mt-1"
          style={{ color: SUBTEXT_COLOR }}
        >
          Ledgiventa needs at least one building to show your dashboard widgets.
        </p>
      </div>

      <Link
        to="/dashboard/buildings/new"
        data-testid="onboarding-banner-cta"
        className="flex-shrink-0 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{ backgroundColor: "#2563eb" }}
      >
        Add building →
      </Link>
    </section>
  );
}
